import { z } from "zod";
import { askUserInput, askUserOutput } from "../agent/ask";
import { appLocale } from "../locale";
/**
 * The WebSocket ticket (`D34`) — the ONE description of it, read by both
 * Workers and by the app.
 *
 * ── WHY IT LIVES IN `@repo/contract` AND NOT IN EITHER WORKER (`T53`) ────────
 * It used to live in `apps/ai/src/auth/ws-ticket.ts`, and `apps/api` re-declared
 * the mint payload locally as a hand-written interface because it could not
 * import out of another app. Two descriptions of one contract, in two bundles,
 * with nothing keeping them in sync — and the mobile client made it four,
 * restating the close codes and the default conversation id in
 * `src/lib/chat/` with comments pointing at the file they were copied from.
 *
 * `packages/shared/` is the only place all three can import, so the contract
 * lives here and the copies are gone. It deliberately does NOT live in
 * `@repo/agents`: `apps/api` would then depend on the agent module just to open
 * a socket.
 *
 * ── WHY A TICKET EXISTS AT ALL ───────────────────────────────────────────────
 * A browser cannot set headers on a WebSocket handshake, so the bearer token
 * cannot travel its usual way. D34 answers that with a single-use, 60-second,
 * opaque ticket in the query string: it is minted by an authenticated call on
 * `apps/api`, and consumed here.
 *
 * ── WHY THE MINT PAYLOAD IS A SCHEMA AND NOT AN INTERFACE ────────────────────
 * Minting happens in `apps/api` and consuming happens here, in a separate
 * bundle. Nothing compares the two descriptions of that payload — not `tsc`,
 * which never sees them together, and not the RPC itself, which structured
 * -clones whatever it is given. That is TRAP T-35 in a new place, so the
 * boundary is PARSED rather than trusted: a drifted field throws on the first
 * mint instead of storing a ticket with a hole in it.
 *
 * ── SECONDS, NEVER MILLISECONDS ──────────────────────────────────────────────
 * Every timestamp here is unix SECONDS, matching the JWT `exp` these values are
 * derived from and the `expires_at` already on the wire. A milliseconds value
 * in one of these fields does not fail: it simply puts the expiry ~50,000 years
 * out, and the single property that makes a ticket in a URL acceptable — that
 * it dies in 60 seconds — is silently gone.
 */
/**
 * 60 seconds, from `D34`. Not a tuning parameter: the ticket appears in URL
 * logs, in proxy logs and in client crash reports, and this number plus
 * single-use is the entire argument for why that is acceptable.
 */
export const WS_TICKET_TTL_SECONDS = 60;
/** The public handshake path on the agent Worker: `WS /chat?ticket=<opaque>`. */
export const CHAT_ROUTE = "/chat";
/**
 * Reading the conversation back, over HTTP: `GET /chat/history?ticket=<opaque>`.
 *
 * ── WHY THIS EXISTS AT ALL (`T-58`) ─────────────────────────────────────────
 * The conversation is already stored — the agent is a Durable Object with
 * SQLite (`new_sqlite_classes`), and the SDK persists every finished answer to
 * its own table whether or not anybody is connected. What was missing was a
 * door. The SDK ships one (`…/get-messages`) and this Worker deliberately
 * 404s it, because reaching the SDK's routes needs no credential at all and
 * exposing them would be a complete auth bypass.
 *
 * So the door is ours, and it is ticketed exactly like the socket: same mint,
 * same single use, same sixty seconds, same object name derived from the
 * CALLER's account — which is what makes a ticket for one account unable to
 * read another's conversation.
 *
 * The reply carries the answer text as it was STORED, control markers and all.
 * The client parses it with the same functions it uses on a live turn, so
 * there is one parser for both paths rather than a second one here that would
 * drift.
 */
export const CHAT_HISTORY_ROUTE = "/chat/history";
/**
 * The query parameters of that handshake. Constants rather than literals so the
 * client, the Worker and the tests cannot drift — a misspelled parameter reads
 * as "no ticket presented", which is a 401 with a correct-looking client.
 */
export const CHAT_TICKET_PARAM = "ticket";
export const CHAT_CONVERSATION_PARAM = "conversation";
/**
 * The SUBJECT SELECTOR (`D74`): which student a trainer's conversation is
 * about. A selector into the roster the edge resolves, never a subject in
 * itself — `apps/ai`'s handshake refuses any value that names nobody the
 * caller reaches. Sent by the app since the trainer's "Pedir à IA um
 * rascunho" (`D270`); before that it lived in `apps/ai` alone.
 */
export const CHAT_SUBJECT_PARAM = "subject";
/**
 * Who the socket belongs to, established at the API edge and carried inward.
 *
 * ── THIS TYPE IS THE MITIGATION FOR TRAP T-11 ────────────────────────────────
 * Edge request properties do not exist inside a Durable Object. `cf.country`
 * and its siblings are `null` there — not an error, `null` — and they are
 * already `null` under local development, so a regression is invisible exactly
 * where it would normally be caught. Nothing downstream of the edge may read
 * `request.cf`; it reads this instead, and this is filled in the one place the
 * properties are real.
 */
export const wsTicketIdentity = z.object({
    /** The `sub` claim of the access token that bought the ticket. */
    accountId: z.string().min(1),
    /**
     * The `exp` of that access token, unix seconds. `D36`: the socket is
     * authenticated once, at the handshake, and closed when this passes.
     */
    authExpiresAt: z.number().int(),
    /**
     * `cf.country` read at the API edge, or `null` when the platform did not
     * supply one (which is every local-development request). A session-anomaly
     * signal, never an authorization input.
     *
     * NULLABLE, NOT OPTIONAL. An omitted field would parse clean and leave the
     * agent unable to tell "the edge had no country" from "nobody remembered to
     * read it" — which is TRAP T-11 reappearing one layer up.
     */
    country: z.string().nullable(),
    /**
     * The user's chosen interface language, from the client's own setting
     * (`D42`). The trainer answers in it (`D43`).
     *
     * REQUIRED, and neither optional nor nullable — unlike `country` directly
     * above, and the difference is the whole point. `country` is nullable
     * because "the edge had no country" is a real state that has to be
     * distinguishable from "nobody read it". There is no such state here: the
     * client always has a value, because `D42` seeds one on first launch. So an
     * absent locale can only mean the envelope was built wrong, and this makes
     * that a parse failure at the boundary rather than a silent default that
     * answers a Brazilian user in English.
     *
     * NOT derived from `country`. A country is not a language, and `D42` rejects
     * geolocation explicitly — it would be wrong for this project's own author,
     * who develops behind a VPN and would be read at the exit node.
     *
     * ── IF YOU ARE READING THIS BECAUSE A MINT IS THROWING ──────────────────────
     * Because the field is required, an `apps/api` deployed before it existed
     * mints without it and `wsTicketMintInput` rejects the call — every socket
     * fails to open until the API Worker catches up. That is deliberate: a ticket
     * that silently carried no language would answer people in the wrong one, and
     * nobody would report it as a bug. `D22` deploys both Workers from the same
     * tag, so the window is one deploy long and closes when that deploy finishes.
     */
    locale: appLocale,
    /** The `sid` claim. Not used for validation — it is what lets a future
     *  "where am I signed in" screen attribute a live socket. */
    sessionId: z.string().min(1),
});
/**
 * What `apps/api` hands to `apps/ai` when a ticket is minted.
 *
 * ── WHY THIS IS PARSED AND NOT MERELY TYPED ──────────────────────────────────
 * The two Workers are separate bundles, so the minting side's idea of this
 * payload is a HAND-WRITTEN declaration that no compiler ever compares against
 * this one. That is TRAP T-35's shape: a renamed or dropped field is not a type
 * error, not a boot error and not a test failure — the ticket is simply stored
 * with a hole in it, every socket refuses to open, and nothing anywhere says
 * why. Parsing at the boundary turns that into a loud throw on the first mint.
 */
export const wsTicketMintInput = z.object({
    /** Unix seconds. Mint time + `WS_TICKET_TTL_SECONDS`. */
    expiresAt: z.number().int(),
    identity: wsTicketIdentity,
    /**
     * `sha256Hex` of the opaque ticket — 64 lowercase hex characters, which is
     * also the check that catches the worst possible mistake on the minting
     * side: sending the PLAINTEXT ticket instead of its digest. **The plaintext
     * never leaves the Worker that minted it**, exactly as refresh tokens are
     * stored (`D34`, `D15`), so a dump of the agent Worker's storage yields
     * nothing presentable.
     */
    ticketHash: z.string().regex(/^[0-9a-f]{64}$/),
});
/**
 * Conversation ids are client-chosen, and they become a Durable Object name —
 * so they are validated rather than trusted. Anchored, bounded, and no `.` or
 * `/`, which would let a name climb out of the segment it is written into.
 */
export const CONVERSATION_ID_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;
/** Used when the client opens a socket without naming a conversation. */
export const DEFAULT_CONVERSATION_ID = "default";
/**
 * The Durable Object instance name for a conversation.
 *
 * ── THE ACCOUNT ID IS IN THE NAME, AND THAT IS THE ACCESS CONTROL ────────────
 * One object per (account, conversation) pair. A ticket for account A therefore
 * cannot address account B's object even when it names B's conversation id: it
 * resolves a different object, which starts empty. This is a structural
 * guarantee rather than a check somebody has to remember to write — there is no
 * code path in which an account id is compared and could be compared wrongly.
 *
 * `__` as the separator because both halves are already constrained to
 * `[a-zA-Z0-9_-]` (a UUID and `CONVERSATION_ID_PATTERN`), so no value of either
 * half can produce the same name as a different pair.
 */
export function agentInstanceName(accountId, conversationId) {
    return `${accountId}__${conversationId}`;
}
/**
 * The account half of a name built above. Returns `null` for anything else, so
 * an object addressed by some other route cannot be mistaken for an
 * authenticated one.
 */
export function accountIdFromInstanceName(name) {
    const separator = name.indexOf("__");
    return separator > 0 ? name.slice(0, separator) : null;
}
/**
 * What `apps/api` hands to `apps/ai` when a conversation is erased (`D153`) —
 * the two halves of the object's name, and nothing else.
 *
 * ── PARSED ON ARRIVAL, LIKE THE MINT PAYLOAD (`T-35`) ────────────────────────
 * The two Workers are separate bundles and nothing compares their ideas of this
 * shape. Here the failure a hole would produce is worse than the mint's: the
 * name is BUILT from these two strings, so `undefined` in either half names an
 * object that has never existed, which erases clean and answers `erased: true`
 * while the real transcript is untouched. Parsing makes that a throw on the
 * first call, naming the field.
 *
 * `strictObject`, so a caller cannot smuggle a third field — say, a subject —
 * into a payload whose whole meaning is "this person's own conversation".
 * `accountId` is a UUID because every account id in this system is one, and
 * `conversationId` is held to {@link CONVERSATION_ID_PATTERN} because it is the
 * same client-chosen segment the handshake validates.
 */
export const eraseConversationInput = z.strictObject({
    accountId: z.string().uuid(),
    conversationId: z.string().regex(CONVERSATION_ID_PATTERN),
});
/**
 * Deliberately not `void`, for the reason `WsTicketMintAck` gives: a `void` RPC
 * gives the caller no evidence the far side did anything, and the failure it
 * would hide is the one `D153` refuses — an index row soft-deleted for an
 * object that still holds every word.
 *
 * A SCHEMA rather than an interface, unlike the mint ack, because the caller
 * PARSES it: `apps/api` runs `eraseConversationAck.parse` on what comes back
 * and soft-deletes the index row only past that line. The literal `true` is
 * therefore not the dead-code hazard it would be on a plain interface — there
 * is no `if (!ack.erased)` for a linter to remove, only a parse that refuses
 * anything else.
 */
export const eraseConversationAck = z.object({ erased: z.literal(true) });
/**
 * The identity envelope travels inward on the INTERNAL request's query string,
 * and the choice of channel is deliberate on both counts.
 *
 * ── WHY NOT HEADERS ──────────────────────────────────────────────────────────
 * A hibernating WebSocket keeps no request headers. `partyserver` persists the
 * upgrade request's URL in the socket's attachment (`connection.uri`) and
 * nothing else, so the URL is the one channel that is still readable after the
 * object has been evicted and woken — which is exactly when the expiry below
 * has to be re-checked. Headers would work at connect time and then silently
 * become unavailable, and the check would degrade to "always allow".
 *
 * ── WHY NOT `connection.setState` ────────────────────────────────────────────
 * It is the obvious answer and it is the wrong one: `state` and `setState` are
 * documented as CONFIGURABLE and are redefined by the Agents SDK to namespace
 * its own connection state. Writing an authorization fact into a slot another
 * layer owns is a collision that would appear as an occasional missing state.
 *
 * ── WHY THIS URL IS NOT THE THING D34 WARNS ABOUT ────────────────────────────
 * D34's concern is a credential in a URL a client holds and logs. This URL is
 * built inside the agent Worker, is never seen by any client, carries no
 * credential — the ticket is deliberately NOT copied into it — and names only
 * an account already established by the handshake.
 */
export const IDENTITY_PARAMS = {
    accountId: "_pg_account",
    authExpiresAt: "_pg_auth_exp",
    country: "_pg_country",
    locale: "_pg_locale",
    sessionId: "_pg_session",
};
/** Writes the envelope onto the internal URL. */
export function writeIdentityParams(url, identity) {
    url.searchParams.set(IDENTITY_PARAMS.accountId, identity.accountId);
    url.searchParams.set(IDENTITY_PARAMS.sessionId, identity.sessionId);
    url.searchParams.set(IDENTITY_PARAMS.authExpiresAt, String(identity.authExpiresAt));
    url.searchParams.set(IDENTITY_PARAMS.locale, identity.locale);
    if (identity.country !== null) {
        url.searchParams.set(IDENTITY_PARAMS.country, identity.country);
    }
}
/**
 * Reads it back inside the agent, from either the connect request's URL or a
 * hibernated connection's stored `uri`.
 *
 * Returns `null` for absent, malformed or unparseable — and the agent treats
 * every one of those as "this connection was never authenticated" and closes
 * it. Failing open here would mean that any request which reached the Durable
 * Object by some other route arrived as an anonymous but accepted session.
 */
export function readIdentityParams(source) {
    if (!source) {
        return null;
    }
    let params;
    try {
        params = new URL(source).searchParams;
    }
    catch {
        return null;
    }
    const accountId = params.get(IDENTITY_PARAMS.accountId);
    const sessionId = params.get(IDENTITY_PARAMS.sessionId);
    // PARSED against the enum, not cast. Anything on this URL is a string, and a
    // locale the app does not ship would reach `buildSystemPrompt` and select a
    // prompt variant that does not exist. `safeParse` makes an unknown tag read
    // as a malformed envelope, which is the same outcome as a missing one.
    const locale = appLocale.safeParse(params.get(IDENTITY_PARAMS.locale));
    // Read as a string first, on purpose. `Number(null)` and `Number("")` are
    // both `0` and both finite, so a coercion here would turn a MISSING value
    // into a valid-looking expiry in 1970 — an envelope that reads as
    // authenticated-but-stale rather than as absent.
    const rawExpiry = params.get(IDENTITY_PARAMS.authExpiresAt);
    if (!(accountId && sessionId && rawExpiry && locale.success)) {
        return null;
    }
    const authExpiresAt = Number(rawExpiry);
    if (!Number.isFinite(authExpiresAt)) {
        return null;
    }
    return {
        accountId,
        authExpiresAt,
        country: params.get(IDENTITY_PARAMS.country),
        locale: locale.data,
        sessionId,
    };
}
/**
 * WebSocket close codes in the application range (4000–4999), so a client can
 * tell "refresh and reconnect" from "something broke" without parsing a string.
 *
 * `D36`: the socket is authenticated once, at the handshake, and closed when
 * the access token that bought its ticket expires. `WS_CLOSE_AUTH_EXPIRED` is
 * the signal for that, and it is the client's cue to refresh, buy a new ticket
 * and reconnect — which is cheap precisely because `D11` makes the stream
 * resumable.
 */
export const WS_CLOSE_AUTH_EXPIRED = 4401;
/** The connection carried no usable identity envelope. Reaching the agent
 *  without one should be impossible; it is closed rather than tolerated. */
export const WS_CLOSE_UNAUTHENTICATED = 4403;
/**
 * The body of `POST /auth/ws-ticket`.
 *
 * The route was bodyless until `D43` gave the trainer a language. The locale
 * rides the TICKET rather than each turn because a WebSocket has exactly one
 * handshake — there is no per-request slot to put it in, which is what the
 * catalogue's `?locale=` uses. Same principle either way: the client tells the
 * server what language it wants, and the server stores nothing.
 */
export const wsTicketInput = z.object({
    /** The APP's stored setting (`D42`), never the device's current language. */
    locale: appLocale,
});
/**
 * The ticket as it comes back on the wire.
 *
 * ── SECONDS, NEVER MILLISECONDS ──────────────────────────────────────────────
 * `expiresAt` is unix SECONDS, matching `expires_at` on the token pair and the
 * JWT `exp` both are cut from. A milliseconds value does not fail: it puts the
 * expiry roughly 50,000 years out, and the single property that makes a
 * credential in a URL acceptable — that it dies in 60 seconds — is silently
 * gone. It is validated as an integer here so a drifted unit is a parse failure
 * at the boundary rather than a ticket that never expires.
 *
 * ── THE TICKET IS NOT CACHEABLE, AND NOTHING HERE CAN ENFORCE THAT ───────────
 * Consumption on the agent Worker deletes the stored ticket UNCONDITIONALLY,
 * before any decision is taken, so a replayed ticket answers 401 exactly like
 * an unknown one. A client that reuses a ticket therefore sees an
 * indistinguishable refusal rather than an error naming the mistake. Buy one
 * per connection attempt; never store one.
 */
export const wsTicketOutput = z.object({
    /** Unix **seconds**. Mint time + `WS_TICKET_TTL_SECONDS`. */
    expiresAt: z.number().int().positive(),
    /** 256-bit CSPRNG, base64url, opaque — never a JWT, and never reusable. */
    ticket: z.string().min(1),
});
/**
 * One stored message, as `GET /chat/history` returns it.
 *
 * `text` is RAW: it still carries the end-of-answer marker (`D77`) and any
 * `ask` block (`D112`). Stripping them here would mean restating that
 * vocabulary in the contract, where it would drift from the agent that writes
 * it; the client already has the parser and applies it to both paths.
 */
export const chatHistoryMessage = z.object({
    /**
     * The `askUser` tool calls this message carries, in the order they were made
     * (`D122`).
     *
     * ── WHY THE WIDGET IS NOT IN `text` ─────────────────────────────────────────
     * `D112` put the question inside the answer, as an `ask` block the client
     * parsed out of the prose. `D122` replaced that with a real tool call, and
     * the parts of a `UIMessage` are where a tool call and its result actually
     * live — so history returns them as structure rather than as something to
     * parse. That is the whole reason a widget now restores on another device.
     *
     * `output` is optional because an UNANSWERED ask is a real, common state: the
     * model asked, the person put the phone down, and the transcript has to come
     * back with the widget still waiting rather than with it silently gone.
     *
     * `toolCallId` is what pairs the answer with the call when the person finally
     * taps — the SDK's own identifier for the part, not one we mint.
     *
     * Optional and absent by default, like `hidden` below: every message stored
     * before this existed has no tool parts, and an absent field is the honest
     * reading of that.
     */
    asks: z
        .array(z.object({
        input: askUserInput,
        output: askUserOutput.optional(),
        toolCallId: z.string(),
    }))
        .optional(),
    /**
     * Sent to the model, never shown to the person — the app's own opening line.
     *
     * Optional and absent by default, because every message persisted before
     * the flag existed has no trace of it and restores visible. That old
     * pollution is cleared by hand, not guessed at here: matching known opening
     * texts would break on the next copy change, silently.
     */
    hidden: z.boolean().optional(),
    id: z.string().min(1),
    role: z.enum(["assistant", "user"]),
    text: z.string(),
});
/**
 * Oldest first, the order the transcript is rendered in.
 *
 * No cursor: a conversation is short today and the whole point of the call is
 * that the screen can rebuild itself in one round trip. When conversations
 * grow, the limit belongs here rather than in a client that would have to
 * discover it.
 */
/**
 * A turn the agent is still answering, as `GET /chat/history` reports it
 * (`D192`).
 *
 * ── WHY THE CLIENT HAS TO BE TOLD, AND CANNOT GUESS ─────────────────────────
 * `D19` holds an answer back until it is whole, so for the length of a
 * generation the stored transcript ends in the person's question with nothing
 * after it — exactly what a turn that DIED looks like. The client used to tell
 * the two apart with timers (fifteen seconds after a resume ACK, five minutes
 * of silence), and every timer that fired on a slow-but-healthy turn told the
 * person their answer was lost while the server went on and stored it. This
 * is the agent saying which of the two it is.
 *
 * `userMessageId` is the question the turn answers, `null` for a continuation
 * (the turn an answered `askUser` widget wakes, which has no new question).
 */
export const chatTurnInFlight = z.object({
    continuation: z.boolean(),
    /** Epoch ms, when the agent began the turn. */
    startedAt: z.number().int().nonnegative(),
    userMessageId: z.string().min(1).nullable(),
});
export const chatHistoryOutput = z.object({
    /**
     * The turn still being answered, or `null` when none is. Optional so a
     * client reading an older agent — which never sends it — reads "unknown",
     * not "nothing in flight" (`D172`'s rule for a new output field).
     */
    inFlight: chatTurnInFlight.nullable().optional(),
    messages: z.array(chatHistoryMessage),
});
//# sourceMappingURL=ws-ticket.js.map